import CommunicatorClientBase, {IStateBase} from '../device/communicatorClientBase';
import {log} from '../log';
import {extendedScpService} from '../scp/extendedScp';
import {scpUtils} from '../scp/scpUtils';
import HIDClient from './hidClient';

let instance: HIDCommunicatorClient | null = null;

const RESPONSE_TIMEOUT_MS = 2 * 1000;
const WRITE_FRAME_RETRY_COUNT = 3;
const WRONG_HEADER_RETRY_COUNT = 1;

interface IState extends IStateBase {
    segmentsResponses: string[];
}

export default class HIDCommunicatorClient extends CommunicatorClientBase<HIDClient, IState> {
    constructor(createNew = false) {
        super();

        if (createNew && instance) {
            instance = null;
        }

        if (instance) {
            return instance;
        }

        instance = this;
        this.isActive = true;
        this.iqosClient = new HIDClient();
        this.resetState();
        this.addOnInputReportListener();
    }

    disable = (): void => {
        this.isActive = false;
        this.clearResponseTimeout();
        this.clearQueue();
    };

    restartListeners = (): void => {
        this.addOnInputReportListener();
    };

    getInitialState(): IState {
        return {
            currentFrame: undefined,
            frames: [],
            onError: undefined,
            onSuccess: undefined,
            responses: [],
            wrongHeaderRetryCount: WRONG_HEADER_RETRY_COUNT,
            timeout: 0,
            skipErrorOnTimeout: undefined,
            segmentsResponses: [],
            isDebug: true,
            retryNumber: 0,
        };
    }

    private addOnInputReportListener = (): void => {
        this.iqosClient.subscribeOnInputReport({onResponse: this.onInputReport});
    };

    private onInputReport = (value: string, reportId: string): void => {
        const {frames, isDebug} = this.state;
        const responseFrame = (reportId + value).toUpperCase();

        this.resetRetries();

        if (isDebug) {
            log.debug(`HIDCommunicatorClient: oninputreport: ${responseFrame}`);
        }

        if (!frames.length) return;

        const isSegmentedFrame = extendedScpService.isSegmentedFrameReportId(reportId);

        if (isSegmentedFrame) {
            this.handleSegmentedInput(responseFrame);
        } else {
            this.handleNonSegmentedInput(responseFrame);
        }
    };

    handleSegmentedInput = (responseFrame: string) => {
        const {isInitial, segmentId} = extendedScpService.getSegmentIdFromSegment(responseFrame);

        if (isInitial) {
            this.state.segmentsResponses = [];
        }

        this.state.segmentsResponses.push(responseFrame);

        const isLastSegment = segmentId === 0;

        if (isLastSegment) {
            this.clearResponseTimeout();
            const response = extendedScpService.reassembleFrame(this.state.segmentsResponses);

            this.state.responses.push(response);
            this.writeFrames();
        }
    };

    handleNonSegmentedInput = (responseFrame: string) => {
        const {frames, responses} = this.state;

        this.clearResponseTimeout();
        const requestFrame = frames[responses.length];
        const isExtendedFrame = extendedScpService.isExtendedFrame(requestFrame);
        const isRequestAndResponseHeaderEqual = isExtendedFrame
            ? true
            : scpUtils.isRequestAndResponseHeaderEqual(requestFrame, responseFrame);

        if (isRequestAndResponseHeaderEqual) {
            const response = isExtendedFrame
                ? extendedScpService.getNetworkLayerFrame(responseFrame)
                : scpUtils.removeRubbishFromFrame(responseFrame);

            this.state.responses.push(response);
            this.writeFrames();
        } else {
            log.info(
                `HIDCommunicatorClient: request: ${requestFrame} and response: ${responseFrame} headers are not equal`
            );
            this.writeFrameAgainIfAllowed();
        }
    };

    writeFrameAgainIfAllowed(): void {
        const {wrongHeaderRetryCount} = this.state;

        log.info(`writeFrameAgainIfAllowed: retry count: ${wrongHeaderRetryCount}`);

        if (wrongHeaderRetryCount > 0) {
            this.state.wrongHeaderRetryCount = wrongHeaderRetryCount - 1;

            this.writeFrames();
        } else {
            this.onFrameResponseError();
        }
    }

    writeFrames(): void {
        const requestIsCancelled = this.stopRequestIfItIsCancelled();

        if (requestIsCancelled) return;

        const isPaused = this.pauseRequestIfThereIsAnotherWithHigherPriority();

        if (isPaused) return;

        const {frames, responses, timeout} = this.state;
        const allFramesHasResponses = !frames || responses.length >= frames.length;

        if (allFramesHasResponses) {
            this.onFramesResponse();
            return;
        }

        const frame = frames[responses.length];
        const isSegmentedFrame = extendedScpService.isExtendedSegmentedFrame(frame);

        if (isSegmentedFrame) {
            this.writeSegmentedFrame(frame);
            return;
        }

        const isExtendedFrame = extendedScpService.isExtendedFrame(frame);
        const frameToWrite = isExtendedFrame ? extendedScpService.addHeadersToAtomicFrame(frame) : frame;

        setTimeout(() => this.writeFrame(frameToWrite), timeout);
    }

    writeSegmentedFrame = (frame: string): void => {
        const segments = extendedScpService.segmentFrame(frame);

        segments.forEach((segment: string) => {
            this.writeSegment(segment);
        });
        this.initResponseTimeout(frame);
    };

    writeSegment = (frame: string): void => {
        this.sendReport(frame);
    };

    writeFrame = (frame: string): void => {
        const result = this.sendReport(frame);

        if (!result) return;
        this.initResponseTimeout(frame);
    };

    sendReport = (frame: string) => {
        if (!this.iqosClient.isDeviceConnected()) {
            this.clearQueue();
            log.debug(`HIDCommunicatorClient: sendReport - writing frame: ${frame} is stopped, device is disconnected`);

            return false;
        }

        const {isDebug} = this.state;
        const reportId = scpUtils.getReportIdDecFromFrame(frame);
        const command = scpUtils.removeReportIdFromFrame(frame);

        this.iqosClient.sendReport({command, reportId, isDebug});
        return true;
    };

    initResponseTimeout = (frame: string): void => {
        const {responseTimeoutMs = RESPONSE_TIMEOUT_MS} = this.state;

        this.clearResponseTimeout();
        this.responseTimeout = setTimeout(() => {
            this.onResponseTimeout(frame);
        }, responseTimeoutMs);
    };

    onResponseTimeout = (frame: string): void => {
        if (!this.isActive) {
            this.clearQueue();
            return;
        }

        const {retryCount = WRITE_FRAME_RETRY_COUNT, skipErrorOnTimeout, retryNumber} = this.state;

        if (retryCount > retryNumber) {
            this.state.retryNumber = retryNumber + 1;

            log.debug(
                `HIDCommunicatorClient: onResponseTimeout, there is no response from device for ${frame} frame. Try to write frame again, retry #${this.state.retryNumber}, skipErrorOnTimeout: ${skipErrorOnTimeout}`
            );

            this.writeFrames();
        } else {
            log.debug(
                `HIDCommunicatorClient: onResponseTimeout, there is no response from device for ${frame} frame. skipErrorOnTimeout: ${skipErrorOnTimeout}`
            );

            if (skipErrorOnTimeout) {
                this.state.responses.push('');
                this.writeFrames();
            } else {
                this.onFrameResponseError();
            }
        }
    };
}
